import config from '../config';
import { getAdminToken } from './adminAuth';

const API_BASE = config.API_BASE;

export const MAX_VIDEO_BYTES = 100 * 1024 * 1024;
export const VIDEO_ACCEPT = 'video/mp4,video/webm,video/quicktime,.mp4,.m4v,.webm,.mov';

const VIDEO_EXT_PATTERN = /\.(mp4|m4v|webm|mov)$/i;

export function validateVideoFile(file) {
  if (!file) return 'No video selected';
  if (!VIDEO_EXT_PATTERN.test(file.name || '')) {
    return `${file.name || 'File'}: use an MP4, WebM, or MOV video`;
  }
  if (file.size > MAX_VIDEO_BYTES) {
    return `${file.name}: video must be 100MB or smaller (this one is ${Math.round(file.size / 1024 / 1024)}MB)`;
  }
  return '';
}

/** Uploads a video with progress reporting (fetch cannot report upload progress). */
export function uploadAdminVideo(file, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE}/api/upload/video`);
    xhr.withCredentials = true;
    const token = getAdminToken();
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded / e.total);
    };
    xhr.onload = () => {
      let data;
      try {
        data = JSON.parse(xhr.responseText || '{}');
      } catch {
        reject(new Error(`Unexpected server response while uploading video (HTTP ${xhr.status})`));
        return;
      }
      if (xhr.status >= 200 && xhr.status < 300 && data.success && data.url) {
        resolve(data.url);
      } else {
        reject(new Error(data.error || `Video upload failed (HTTP ${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error('Network error while uploading video'));

    const formData = new FormData();
    formData.append('video', file);
    xhr.send(formData);
  });
}

/** Grabs a JPEG still from a local video file, used as the non-hover image. */
export function captureVideoPoster(file, { seekTo = 0.5, maxWidth = 1600 } = {}) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const video = document.createElement('video');
    let settled = false;

    const finish = (fn, value) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      URL.revokeObjectURL(objectUrl);
      video.removeAttribute('src');
      video.load();
      fn(value);
    };
    const fail = (message) => finish(reject, new Error(message));

    const timer = window.setTimeout(() => fail('Timed out reading the video to create a still image'), 20000);

    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';

    video.addEventListener('error', () => {
      fail(
        "Your browser couldn't decode this video, so some visitors may not be able to play it either. " +
          'Exporting it as MP4 (H.264) gives the best compatibility'
      );
    });

    video.addEventListener(
      'loadedmetadata',
      () => {
        const duration = video.duration;
        video.currentTime = Number.isFinite(duration) && duration > 0 ? Math.min(seekTo, duration / 2) : seekTo;
      },
      { once: true }
    );

    video.addEventListener(
      'seeked',
      () => {
        const width = video.videoWidth;
        const height = video.videoHeight;
        if (!width || !height) {
          fail('Could not read video dimensions to create a still image');
          return;
        }
        const scale = Math.min(1, maxWidth / width);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(width * scale);
        canvas.height = Math.round(height * scale);
        canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              fail('Could not create a still image from this video');
              return;
            }
            const baseName = (file.name || 'video').replace(/\.[^.]+$/, '');
            finish(resolve, new File([blob], `${baseName}-still.jpg`, { type: 'image/jpeg' }));
          },
          'image/jpeg',
          0.86
        );
      },
      { once: true }
    );

    video.src = objectUrl;
  });
}
